import type { Point, ViewportSize } from '@/types';

/**
 * Geometry utilities for clock calculations.
 */

/**
 * Calculate the maximum radius from a center point to the closest viewport edge.
 * Used as a fallback/minimum bound.
 */
export const getMaxRadiusToEdge = (
  center: Point,
  viewport: ViewportSize
): number => {
  const distanceToLeft = center.x;
  const distanceToRight = viewport.width - center.x;
  const distanceToTop = center.y;
  const distanceToBottom = viewport.height - center.y;

  return Math.min(distanceToLeft, distanceToRight, distanceToTop, distanceToBottom);
};

/**
 * Calculate the distance from center to the viewport edge at a specific angle.
 * Angle is in degrees, clockwise from 12 o'clock (0° = up).
 * 
 * This enables dynamic hand lengths that vary as hands sweep around the clock.
 */
export const getRadiusAtAngle = (
  center: Point,
  viewport: ViewportSize,
  angleDegrees: number
): number => {
  // Convert clock angle to standard math angle (radians)
  // Clock: 0° = up, clockwise
  // Math: 0° = right, counter-clockwise
  const angleRad = ((angleDegrees - 90) * Math.PI) / 180;
  
  // Ray direction from center
  const dirX = Math.cos(angleRad);
  const dirY = Math.sin(angleRad);
  
  // Calculate intersection with each edge and find the closest
  let minDistance = Infinity;
  
  // Left edge (x = 0)
  if (dirX < 0) {
    const t = -center.x / dirX;
    const y = center.y + t * dirY;
    if (y >= 0 && y <= viewport.height) {
      minDistance = Math.min(minDistance, t);
    }
  }
  
  // Right edge (x = viewport.width)
  if (dirX > 0) {
    const t = (viewport.width - center.x) / dirX;
    const y = center.y + t * dirY;
    if (y >= 0 && y <= viewport.height) {
      minDistance = Math.min(minDistance, t);
    }
  }
  
  // Top edge (y = 0)
  if (dirY < 0) {
    const t = -center.y / dirY;
    const x = center.x + t * dirX;
    if (x >= 0 && x <= viewport.width) {
      minDistance = Math.min(minDistance, t);
    }
  }
  
  // Bottom edge (y = viewport.height)
  if (dirY > 0) {
    const t = (viewport.height - center.y) / dirY;
    const x = center.x + t * dirX;
    if (x >= 0 && x <= viewport.width) {
      minDistance = Math.min(minDistance, t);
    }
  }
  
  return minDistance === Infinity ? 0 : minDistance;
};

/**
 * Calculate hand length for a given base ratio.
 * Applies the center offset to create a gap between hands and center dot.
 */
export const calculateHandLength = (
  maxRadius: number,
  ratio: number,
  centerOffset: number
): number => {
  return Math.max(0, (maxRadius - centerOffset) * ratio);
};

/**
 * Clamp a value between min and max.
 */
export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Calculate distance between two points.
 */
export const distance = (p1: Point, p2: Point): number => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Find the intersection distance from a ray to a path of line segments.
 * Returns the minimum distance to any segment intersection, or Infinity if no intersection.
 * 
 * @param center The ray origin point
 * @param angleDegrees The ray direction in clock degrees (0° = up, clockwise)
 * @param path Array of points forming the path
 * @returns Distance to nearest intersection, or Infinity
 */
export const getRayPathIntersection = (
  center: Point,
  angleDegrees: number,
  path: Point[]
): number => {
  if (path.length < 2) return Infinity;

  // Convert clock angle to ray direction
  const angleRad = ((angleDegrees - 90) * Math.PI) / 180;
  const dirX = Math.cos(angleRad);
  const dirY = Math.sin(angleRad);

  let minDistance = Infinity;

  // Check intersection with each segment
  for (let i = 0; i < path.length - 1; i++) {
    const p1 = path[i];
    const p2 = path[i + 1];
    
    if (!p1 || !p2) continue;

    const intersection = raySegmentIntersection(center, dirX, dirY, p1, p2);
    if (intersection !== null && intersection < minDistance) {
      minDistance = intersection;
    }
  }

  return minDistance;
};

/**
 * Calculate ray-segment intersection using parametric form.
 * Returns distance along ray if intersection exists, null otherwise.
 */
const raySegmentIntersection = (
  rayOrigin: Point,
  rayDirX: number,
  rayDirY: number,
  p1: Point,
  p2: Point
): number | null => {
  const segDirX = p2.x - p1.x;
  const segDirY = p2.y - p1.y;

  // Cross product of ray direction and segment direction
  const cross = rayDirX * segDirY - rayDirY * segDirX;

  // Parallel check
  if (Math.abs(cross) < 0.0001) {
    return null;
  }

  const dx = p1.x - rayOrigin.x;
  const dy = p1.y - rayOrigin.y;

  // Parameter along ray (distance)
  const t = (dx * segDirY - dy * segDirX) / cross;

  // Parameter along segment (0-1 means on segment)
  const u = (dx * rayDirY - dy * rayDirX) / cross;

  // Check if intersection is in front of ray and on segment
  if (t > 0 && u >= 0 && u <= 1) {
    return t;
  }

  return null;
};
